import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { getOt, accionOt, getPersonasArea } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { entraAOt } from '../espacios'
import { formatFecha } from '../unidades/tipos'
import { EstadoOt, Semaforo } from './Etiquetas'

// A maintenance parte's work order (OT-<n>P), inside the parte's panel: its
// state and traffic light, and the planning the maintenance lead does
// (responsable, priority, committed date -- none of it holds the work).
// The parte drives it: only someone starting work on the parte starts it
// (there is no button), closing the parte closes it (backend ot_partes.py).
export default function OtDeParte({ parte, onCambio }) {
  const { t } = useTranslation('operations')
  const navigate = useNavigate()
  const { user } = useAuth()
  const [ot, setOt] = useState(null)
  const [error, setError] = useState('')
  const ref = parte?.ot

  useEffect(() => {
    if (!ref?.id) return
    let vivo = true
    getOt(ref.id).then(o => { if (vivo) setOt(o) }).catch(() => { if (vivo) setOt(null) })
    return () => { vivo = false }
  }, [ref?.id, ref?.estado])

  if (!ref) return null
  const veTablero = entraAOt(user)
  const puedePlanificar = ot?.acciones?.some(a => a.accion === 'planificar_mantenimiento')

  const actuar = async (accion, datos = {}) => {
    setError('')
    try { const o = await accionOt(ot.id, accion, datos); setOt(o); onCambio?.(o) }
    catch (err) {
      const d = err.response?.data?.detail
      setError(d?.code === 'validacion'
        ? t('ot.faltanDatos', { lista: d.faltan.map(f => t(`ot.faltan.${f}`, { defaultValue: f })).join(', ') })
        : d?.message || t('ot.panel.fallo'))
      throw err
    }
  }

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 px-3 py-2.5 space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-xs text-gray-500">{t('ot.parte.titulo')}</span>
        <span className="font-mono font-semibold text-gray-900">{ref.codigo}</span>
        <EstadoOt estado={ot?.estado || ref.estado} />
        {ot?.semaforo && <Semaforo semaforo={ot.semaforo} conTexto />}
        <span className="ml-auto flex items-center gap-2">
          {veTablero && (
            <button type="button" onClick={() => navigate(`/operations/planta/ot?ot=${ref.id}`)}
              className="text-xs font-medium text-indigo-700 hover:underline">{t('ot.parte.verEnTablero')}</button>
          )}
        </span>
      </div>
      {ot && (puedePlanificar
        ? <PlanificarMantenimiento ot={ot} t={t} onGuardar={datos => actuar('planificar_mantenimiento', datos)} />
        : <ResumenPlan ot={ot} t={t} />)}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  )
}

function ResumenPlan({ ot, t }) {
  const partes = [
    ot.responsable && `${t('ot.campos.responsable')}: ${ot.responsable.nombre}`,
    ot.prioridad && `${t('ot.campos.prioridad')}: ${t(`ot.prioridades.${ot.prioridad}`)}`,
    ot.fecha_compromiso && `${t('ot.campos.fecha_compromiso')}: ${formatFecha(ot.fecha_compromiso)}`,
  ].filter(Boolean)
  return <p className="text-xs text-gray-600">{partes.length ? partes.join(' · ') : t('ot.parte.sinPlan')}</p>
}

// The maintenance lead's planning, also in the OT's own panel: whatever is
// set is checked (a committed date not past), nothing is required.
export function PlanificarMantenimiento({ ot, t, onGuardar }) {
  const [personas, setPersonas] = useState([])
  const [f, setF] = useState({
    responsable_id: ot.responsable?.id ?? '', prioridad: ot.prioridad || '', fecha_compromiso: ot.fecha_compromiso || '',
  })
  const [guardando, setGuardando] = useState(false)
  const [listo, setListo] = useState(false)
  useEffect(() => { getPersonasArea('mantenimiento').then(setPersonas).catch(() => {}) }, [])
  const cambio = (k, v) => { setF(x => ({ ...x, [k]: v })); setListo(false) }
  const guardar = async () => {
    const datos = {}
    if (f.responsable_id !== '' && Number(f.responsable_id) !== ot.responsable?.id) datos.responsable_id = Number(f.responsable_id)
    if (f.prioridad && f.prioridad !== ot.prioridad) datos.prioridad = f.prioridad
    if (f.fecha_compromiso && f.fecha_compromiso !== ot.fecha_compromiso) datos.fecha_compromiso = f.fecha_compromiso
    if (!Object.keys(datos).length) return
    setGuardando(true)
    try { await onGuardar(datos); setListo(true) } catch { /* the caller shows why */ }
    setGuardando(false)
  }
  const sel = 'w-full border border-gray-200 rounded-md px-2 py-1 text-xs bg-white'
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_8rem_9rem_auto] gap-2 items-end">
      <label className="text-[11px] text-gray-500">{t('ot.campos.responsable')}
        <select className={sel} value={f.responsable_id} onChange={e => cambio('responsable_id', e.target.value)}>
          <option value="">—</option>
          {personas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
        </select>
      </label>
      <label className="text-[11px] text-gray-500">{t('ot.campos.prioridad')}
        <select className={sel} value={f.prioridad} onChange={e => cambio('prioridad', e.target.value)}>
          <option value="">—</option>
          {['critica', 'alta', 'media', 'baja'].map(p => <option key={p} value={p}>{t(`ot.prioridades.${p}`)}</option>)}
        </select>
      </label>
      <label className="text-[11px] text-gray-500">{t('ot.campos.fecha_compromiso')}
        <input type="date" className={sel} value={f.fecha_compromiso} onChange={e => cambio('fecha_compromiso', e.target.value)} />
      </label>
      <button type="button" onClick={guardar} disabled={guardando}
        className="text-xs font-medium bg-gray-900 text-white rounded-md px-3 py-1.5 hover:bg-gray-800 disabled:opacity-40">
        {listo ? t('ot.parte.guardado') : t('ot.parte.planificar')}
      </button>
    </div>
  )
}
